import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Search, Briefcase, Sparkles, Users, Calendar, MessageSquare, CreditCard, Settings as SettingsIcon, Store, Loader2, Activity, Inbox, Palette, Mail, Gift, MessageCircle, Send,
} from "lucide-react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";

const EmployerLayout = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login"); return; }
        if (user.role !== "employer") {
            navigate(user.role === "professional" ? "/professional/dashboard" : "/");
        }
    }, [user, loading, navigate]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 size={22} className="animate-spin text-emerald-500" />
            </div>
        );
    }

    // Employer OWNERS whose email domain does not match their company website
    // (or who signed up with a free-email address) land in ``pending_approval``
    // until a Simonara admin verifies them. Team members invited by an already-
    // approved Owner never see this because they're created with ``active``.
    if (user.status === "pending_approval") {
        return (
            <div
                data-testid="employer-pending-approval"
                className="min-h-screen bg-background flex items-center justify-center px-6"
            >
                <div className="max-w-md w-full text-center">
                    <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30">
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h1 className="text-2xl font-semibold text-foreground">Employer workspace under review</h1>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                        Thanks for signing up. Employer accounts on Simonara are
                        verified before they get access to talent data — we do this
                        to protect the professionals on our platform. Someone from
                        our team will confirm your workspace and unlock it shortly.
                        You&apos;ll get an email at{" "}
                        <span className="font-medium text-foreground">{user.email}</span>{" "}
                        the moment we do.
                    </p>
                    <p className="mt-4 text-xs text-muted-foreground">
                        The fastest way through review: sign up with your work email
                        (e.g. <em>you@yourcompany.com</em>) — the system verifies you
                        automatically. Questions? Reply to any Simonara email or write to{" "}
                        <a href="mailto:hello@simonaratalent.us" className="text-emerald-700 dark:text-emerald-400 underline">
                            hello@simonaratalent.us
                        </a>.
                    </p>
                </div>
            </div>
        );
    }

    if (user.status === "rejected") {
        return (
            <div
                data-testid="employer-rejected"
                className="min-h-screen bg-background flex items-center justify-center px-6"
            >
                <div className="max-w-md w-full text-center">
                    <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/30">
                        <svg className="h-6 w-6 text-rose-600 dark:text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-semibold text-foreground">Employer account not approved</h1>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                        After review, our team couldn&apos;t verify that you represent
                        the company you signed up for. If this is a mistake, please
                        reply to us from your work email address so we can confirm
                        quickly, or write to{" "}
                        <a href="mailto:hello@simonaratalent.us" className="text-emerald-700 dark:text-emerald-400 underline">
                            hello@simonaratalent.us
                        </a>.
                    </p>
                </div>
            </div>
        );
    }

    // Permission-driven sidebar. Each item declares the capability required to
    // see it; items are filtered out per user's employer_permission. This is
    // UX-only; the backend enforces the real gate on each endpoint.
    const rawSidebar = [
        {
            section: "Hire",
            items: [
                { to: "/employer/dashboard", exact: true, icon: LayoutDashboard, label: "Dashboard", testId: "esidebar-dashboard", cap: "view_dashboard" },
                { to: "/employer/find-talent", icon: Search, label: "Find Talent", testId: "esidebar-find-talent", cap: "review_candidates" },
                { to: "/employer/jobs", icon: Briefcase, label: "Jobs", testId: "esidebar-jobs", cap: "manage_jobs" },
                { to: "/employer/applications", icon: Inbox, label: "Applications", testId: "esidebar-applications", cap: "view_candidates" },
                { to: "/employer/inbox", icon: MessageCircle, label: "Reservation Inbox", testId: "esidebar-inbox", cap: "view_candidates" },
                { to: "/employer/messages", icon: Send, label: "Messages", testId: "esidebar-messages", cap: "view_candidates" },
                { to: "/employer/briefs", icon: Sparkles, label: "AI Hiring Briefs™", testId: "esidebar-briefs", cap: "manage_jobs" },
                { to: "/employer/pipeline", icon: Users, label: "Candidate Pipeline", testId: "esidebar-pipeline", cap: "view_candidates" },
            ],
        },
        {
            section: "Operations",
            items: [
                { to: "/employer/marketplace", icon: Store, label: "Talent Marketplace", testId: "esidebar-marketplace", cap: "review_candidates" },
                { to: "/employer/scheduling", icon: Calendar, label: "Interview Scheduling", testId: "esidebar-scheduling", cap: "leave_feedback", comingSoon: true },
                { to: "/employer/team", icon: MessageSquare, label: "Team", testId: "esidebar-team", cap: "view_dashboard" },
                { to: "/employer/branding", icon: Palette, label: "Company Branding", testId: "esidebar-branding", cap: "view_dashboard" },
                { to: "/employer/scheduler", icon: Calendar, label: "Scheduler", testId: "esidebar-scheduler", cap: "view_dashboard" },
                { to: "/employer/templates", icon: Mail, label: "Auto-Replies", testId: "esidebar-templates", cap: "view_dashboard" },
                { to: "/employer/referrals", icon: Gift, label: "Refer & Earn", testId: "esidebar-referrals", cap: "view_dashboard" },
                { to: "/employer/activity", icon: Activity, label: "Activity", testId: "sidebar-activity", cap: "view_dashboard" },
                { to: "/employer/billing", icon: CreditCard, label: "Billing", testId: "sidebar-billing", cap: "manage_billing" },
                { to: "/settings", icon: SettingsIcon, label: "Settings", testId: "esidebar-settings", cap: "view_dashboard" },
            ],
        },
    ];

    // Filter items by the current user's employer_permission. Empty sections
    // are hidden entirely.
    const sidebar = rawSidebar
        .map((sec) => ({
            ...sec,
            items: sec.items.filter((it) => !it.cap || hasCapability(user, it.cap)),
        }))
        .filter((sec) => sec.items.length > 0);

    return <DashboardLayout sidebar={sidebar} />;
};

export default EmployerLayout;
